import { Catalog } from '@/ts/utils/resolver/interfaces/resolver';
import { getInjection } from '@/ts/utils/injections';
import { debug } from '@/ts/utilities';
import { DEBUG_MODE } from '@/ts/enums';

const MAX_TRIES = 60;
const INTERVAL = 100; //ms
/**
 * This class allow async communication of callbacks through a mechanism of client-server registration.
 * Any callback at any point in time may request to serve or to be served on a specific unique ID.
 * When at any point in time both a server and a client are registered on the same ID, both callbacks are called to exchange their data.
 * This function doesn't know anything about the nature or the purpose of the communication, so that it can be used to synchronize callbacks from
 * different sources.
 */
export class Resolver {
    public static catalog: Catalog;

    constructor() {
        Resolver.catalog = {};
    }

    exec(callbacks: Function[], args: any[], resolve: Function, reject: Function) {
        Promise.all(callbacks.map(callback => callback(...args)))
            .then(data => {
                const ret = data.length === 1 ? data.pop() : data;
                resolve(ret);
            })
            .catch(err => {
                reject(err);
            });
    }

    run(id: string, ...args: any): Promise<any> {
        const start = Date.now();
        Resolver.catalog[id] = Resolver.catalog[id] || {
            tries: 0,
            callbacks: [],
        };

        let pointer = Resolver.catalog[id];
        return new Promise((resolve, reject) => {
            if (pointer.callbacks.length) {
                this.exec(pointer.callbacks, args, resolve, reject);
            } else {
                const $this = this;
                (function () {
                    const interval = setInterval(() => {
                        pointer = Resolver.catalog[id];
                        pointer.tries++;
                        if (pointer.tries <= MAX_TRIES && pointer.callbacks.length) {
                            clearInterval(interval);
                            $this.exec(pointer.callbacks, args, resolve, reject);
                        } else if (pointer.tries > MAX_TRIES) {
                            clearInterval(interval);
                            debug(`Max tries reached id: ${id}`, DEBUG_MODE.ERROR);
                            reject(id);
                        }
                    }, INTERVAL);
                })();
            }
        }).finally(() => {
            debug(`Resolved id: ${id}, tries: ${pointer.tries}, time: ${Date.now() - start}ms`, DEBUG_MODE.NOTICE);
        });
    }
}

export class ResolverHandler {
    id: string;

    constructor(id: string) {
        this.id = id;
        Resolver.catalog[id] = Resolver.catalog[id] || {
            tries: 0,
            callbacks: [],
        };
    }

    static retrieveInjection(id: string) {
        return getInjection[id] || false;
    }

    register(callback: Function) {
        Resolver.catalog[this.id].callbacks.push(callback);
    }
}

//@ts-ignore;
window['ResolverHandler'] = ResolverHandler;
